import { configureStore } from "@reduxjs/toolkit";
import { setAutoFreeze } from "immer";
import { analyticsSlice, type AnalyticsState } from "@/features/analytics/slice";
import { configurationSlice } from "@/features/configuration/slice";
import { shellSlice, type ShellState } from "@/features/shell/slice";
import { sessionSlice, type SessionState } from "@/features/session/slice";
import { baseApi } from "@/shared/api/baseApi";
import { legacyBridgeMiddleware } from "./legacyBridge";
import { legacySlice, type LegacyState } from "./legacySlice";

// Le viste legacy mutano in place gli oggetti letti dai getter (es. banner.name = value):
// con lo stato congelato da Immer lancerebbero TypeError. Si riattiva in fase 8.
setAutoFreeze(false);

const reducer = {
  legacy: legacySlice.reducer,
  analytics: analyticsSlice.reducer,
  session: sessionSlice.reducer,
  shell: shellSlice.reducer,
  [baseApi.reducerPath]: baseApi.reducer,
};

export function makeStore(preloadedState?: {
  legacy?: LegacyState;
  analytics?: AnalyticsState;
  session?: SessionState;
  shell?: ShellState;
}) {
  return configureStore({
    reducer,
    // Le chiavi omesse usano il default del proprio slice (createSlice le gestisce comunque a runtime):
    // il cast serve solo a riconciliare con il tipo di `reducer`, che RTK non rende opzionale ovunque.
    preloadedState: preloadedState as
      | { legacy: LegacyState; analytics: AnalyticsState; session: SessionState; shell: ShellState }
      | undefined,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        // Lo stato legacy contiene Date e viene mutato fuori dai reducer: i check di RTK lo
        // segnalerebbero a ogni azione. Si esclude solo lo slice legacy (ignoredPaths) e le sue
        // azioni (ignoredActions), non tutte le azioni dello store: RTK Query resta controllato.
        // Si toglie in fase 8.
        serializableCheck: {
          ignoredPaths: ["legacy"],
          ignoredActions: [
            ...Object.values(legacySlice.actions).map((action) => action.type),
            // Le configurazioni degli shop possono contenere valori non serializzabili come il legacy.
            ...Object.values(configurationSlice.actions).map((action) => action.type),
          ],
        },
        immutableCheck: { ignoredPaths: ["legacy"] },
      })
        .prepend(legacyBridgeMiddleware.middleware)
        .concat(baseApi.middleware),
  });
}

export const store = makeStore();
export type AppStore = ReturnType<typeof makeStore>;
export type RootState = ReturnType<AppStore["getState"]>;
export type AppDispatch = AppStore["dispatch"];
